import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import MuiCheckbox from '@mui/material/Checkbox'

import { sortByKey } from '../../util/sorting'
import { theme } from '../../themes/theme'
import { Divider } from '../styles/Containers'
import { TableHeaderButton } from '../common/Button'
import BodyText from '../styles/Text'
import TaskRow, { taskTableColumns } from './TaskRow'

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-bottom: 8px;
`

const AssignedFilter = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.text.body};
  cursor: pointer;
`

const SmallCheckbox = styled(MuiCheckbox)`
  padding: 0;

  svg {
    height: 18px;
    width: 18px;
  }
`

const Table = styled.div`
  display: grid;
  grid-template-columns: ${taskTableColumns};
  row-gap: 15px;
`

const Headings = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;
`

const Heading = styled.div`
  font-size: 16px;
  line-height: 20px;
`

const AssignedHeading = styled(Heading)`
  grid-column: 2;
`

const TaskHeading = styled(Heading)`
  grid-column: 4;
`

const FoiIdHeading = styled(Heading)`
  grid-column: 7;
`

const DescriptionHeading = styled(Heading)`
  grid-column: 8;
  justify-self: center;
  width: min(100%, 250px);
`

const DueDateHeading = styled(Heading)`
  grid-column: 9;
  justify-self: end;
`

const Empty = styled(BodyText)`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding-top: 15px;
`

const HeaderButton = styled(TableHeaderButton)`
  font-size: 16px;
  font-weight: 400;
  white-space: nowrap;

  svg {
    padding-top: 0;
  }
`

function compareDueDate(a, b) {
  if (!a.dueDate && !b.dueDate) return 0
  if (!a.dueDate) return 1
  if (!b.dueDate) return -1
  return a.dueDate - b.dueDate
}

const SORT_KEYS = {
  dueDate: compareDueDate,
  secondaryId: sortByKey('secondaryId')
}

function withSortFields(task) {
  return {
    ...task,
    dueDate: task.foi.dueDate,
    secondaryId: task.foi.secondaryId
  }
}

function TaskList({ tasks, currentUserId, defaultShowOnlyAssigned = true }) {
  const [showOnlyAssigned, setShowOnlyAssigned] = useState(defaultShowOnlyAssigned)
  const [sortBy, setSortBy] = useState('dueDate')
  const [sortAscending, setSortAscending] = useState(true)

  const filteredTasks = useMemo(
    () => (showOnlyAssigned ? tasks.filter(task => task.assignedTo?.id === currentUserId) : tasks),
    [tasks, showOnlyAssigned, currentUserId]
  )

  const sortedTasks = useMemo(() => {
    let next = filteredTasks.map(withSortFields).sort(SORT_KEYS[sortBy])
    if (!sortAscending) next = [...next].reverse()
    return next
  }, [filteredTasks, sortBy, sortAscending])

  function sort(key) {
    if (key === sortBy) {
      setSortAscending(prev => !prev)
    } else {
      setSortBy(key)
      setSortAscending(true)
    }
  }

  return (
    <List>
      <Toolbar>
        <AssignedFilter>
          <SmallCheckbox checked={showOnlyAssigned} onChange={event => setShowOnlyAssigned(event.target.checked)} />
          Show only your assigned tasks
        </AssignedFilter>
      </Toolbar>
      <Divider />
      <Table>
        <Headings>
          <AssignedHeading>Assigned</AssignedHeading>
          <TaskHeading>Task</TaskHeading>
          <FoiIdHeading>
            <HeaderButton variant="text" onClick={() => sort('secondaryId')} active={(sortBy === 'secondaryId').toString()}>
              FOI ID <UnfoldMoreIcon />
            </HeaderButton>
          </FoiIdHeading>
          <DescriptionHeading>FOI Description</DescriptionHeading>
          <DueDateHeading>
            <HeaderButton variant="text" onClick={() => sort('dueDate')} active={(sortBy === 'dueDate').toString()}>
              FOI Due Date <UnfoldMoreIcon />
            </HeaderButton>
          </DueDateHeading>
        </Headings>
        {sortedTasks.length > 0 ? sortedTasks.map(task => <TaskRow key={task.id} task={task} />) : <Empty>No tasks available</Empty>}
      </Table>
    </List>
  )
}

TaskList.propTypes = {
  tasks: PropTypes.array.isRequired,
  currentUserId: PropTypes.string,
  defaultShowOnlyAssigned: PropTypes.bool
}

export default TaskList
